/**
 * VideoAssistantPanel Component
 *
 * Chat panel that appears on the right side of VideoPage.
 * Users describe videos in natural language, and the AI fills
 * the VideoConfigForm automatically.
 *
 * Desktop: side panel (380px width)
 * Mobile: fullscreen overlay
 */

import { useState, useRef, useEffect, useCallback, memo } from 'react';
import { X, Send, Sparkles, Trash2, CheckCircle, ChevronDown } from 'lucide-react';
import { useVideoAssistant } from '@/hooks/useVideoAssistant';
import { useLLMModels } from '@/hooks/useLLMModels';
import { useRequireAuth } from '@/hooks/useRequireAuth';
import { useTranslation } from '@/hooks/useTranslation';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { LoginModal } from '@/components/auth';
import { VIDEO_ASSISTANT_DEFAULT_PROVIDER } from '@/constants/videoAssistant';
import type { VideoConfig } from '@/types';
import type { ChatMessage } from '@/hooks/useVideoAssistant';

interface VideoAssistantPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onConfigUpdate: (config: Partial<VideoConfig>) => void;
}

export const VideoAssistantPanel = memo(function VideoAssistantPanel({
  isOpen,
  onClose,
  onConfigUpdate,
}: VideoAssistantPanelProps) {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();
  const { requireAuth, isLoginModalOpen, loginMessage, closeLoginModal } = useRequireAuth();

  const [inputText, setInputText] = useState('');
  const [llmProvider] = useState(VIDEO_ASSISTANT_DEFAULT_PROVIDER);
  const [isModelSelectorOpen, setIsModelSelectorOpen] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const {
    availableModels,
    selectedModel: llmModel,
    setSelectedModel,
  } = useLLMModels(llmProvider, undefined, { context: 'chat' });

  const { messages, isLoading, isStreaming, error, sendMessage, clearMessages } = useVideoAssistant(
    {
      llmProvider,
      llmModel,
      onConfigUpdate,
    }
  );

  // Auto-scroll to bottom when messages change (scroll only within the messages container)
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container && messages.length > 0) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  // Focus input when panel opens — preserve scroll position of .app-content container
  useEffect(() => {
    if (isOpen && !isMobile) {
      setTimeout(() => {
        // Save scroll position of the actual scroll container (.app-content has overflow-y-auto)
        const scrollContainer = document.querySelector('.app-content');
        const savedScrollTop = scrollContainer?.scrollTop ?? 0;
        const savedWindowY = window.scrollY;

        inputRef.current?.focus({ preventScroll: true });

        // Restore scroll if browser moved it despite preventScroll
        requestAnimationFrame(() => {
          if (scrollContainer && scrollContainer.scrollTop !== savedScrollTop) {
            scrollContainer.scrollTop = savedScrollTop;
          }
          if (window.scrollY !== savedWindowY) {
            window.scrollTo({ top: savedWindowY });
          }
        });
      }, 300);
    }
  }, [isOpen, isMobile]);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleSend = useCallback(() => {
    const text = inputText.trim();
    if (!text || isLoading) return;

    requireAuth(
      async () => {
        setInputText('');
        await sendMessage(text);
      },
      t('auth.loginRequired.chat', { defaultValue: 'Please sign in to use the AI assistant' })
    );
  }, [inputText, isLoading, requireAuth, sendMessage, t]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  // Keep mounted (preserves chat history) but hide with CSS when closed
  if (!isOpen) {
    return <div className="hidden" aria-hidden="true" />;
  }

  // Desktop: floating popup anchored bottom-right (persists across all tabs).
  // Mobile: fullscreen overlay.
  const panelClasses = isMobile
    ? 'fixed inset-0 z-50 flex flex-col bg-secondary-900'
    : 'fixed bottom-6 right-6 z-50 flex flex-col w-[380px] max-w-[calc(100vw-3rem)] h-[600px] max-h-[calc(100vh-7rem)] rounded-2xl border border-secondary-700 bg-secondary-900/95 backdrop-blur-md shadow-2xl shadow-black/40 overflow-hidden';

  return (
    <>
      {/* Mobile backdrop */}
      {isMobile && <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} />}

      <div className={panelClasses} data-testid="video-assistant-panel">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-secondary-700 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary-400" />
            <h3 className="text-sm font-semibold text-white">
              {t('videoAssistant.title', { defaultValue: 'Lina' })}
            </h3>
          </div>
          <div className="flex items-center gap-1">
            {messages.length > 0 && (
              <button
                onClick={clearMessages}
                className="p-1.5 rounded-lg hover:bg-secondary-700 transition-colors"
                title={t('videoAssistant.clearChat', { defaultValue: 'Clear chat' })}
              >
                <Trash2 className="h-4 w-4 text-secondary-400" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-secondary-700 transition-colors"
              title={t('common.close', { defaultValue: 'Close' })}
            >
              <X className="h-4 w-4 text-secondary-400" />
            </button>
          </div>
        </div>

        {/* Model selector (collapsible) */}
        <div className="px-4 py-2 border-b border-secondary-700/50 flex-shrink-0">
          <button
            onClick={() => setIsModelSelectorOpen((prev) => !prev)}
            className="flex items-center gap-1.5 text-xs text-secondary-400 hover:text-secondary-200 transition-colors w-full"
          >
            <span className="truncate">
              {availableModels.find((m) => m.value === llmModel)?.label ||
                t('videoAssistant.model', { defaultValue: 'Model' })}
            </span>
            <ChevronDown
              className={`h-3 w-3 flex-shrink-0 transition-transform ${isModelSelectorOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {isModelSelectorOpen && (
            <select
              value={llmModel}
              onChange={(e) => {
                setSelectedModel(e.target.value);
                setIsModelSelectorOpen(false);
              }}
              className="mt-1.5 w-full bg-secondary-800 border border-secondary-600 rounded-lg px-2 py-1.5 text-xs text-secondary-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              {availableModels
                .filter((m) => !m.disabled)
                .map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
            </select>
          )}
        </div>

        {/* Messages area - scrolls independently from the page */}
        <div ref={messagesContainerRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {/* Welcome message when empty */}
          {messages.length === 0 && (
            <div className="text-center py-8">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center mx-auto mb-3 shadow-lg shadow-primary-500/20">
                <Sparkles className="h-7 w-7 text-white" />
              </div>
              <h4 className="text-base font-semibold text-white mb-1">
                {t('videoAssistant.welcomeGreeting', { defaultValue: "Hi, I'm Lina!" })}
              </h4>
              <p className="text-xs text-secondary-400 mb-3">
                {t('videoAssistant.welcomeSubtitle', { defaultValue: 'Your AI video assistant' })}
              </p>
              <p className="text-sm text-secondary-300 max-w-[280px] mx-auto">
                {t('videoAssistant.welcome', {
                  defaultValue:
                    "Tell me what kind of video you'd like to create and I'll configure everything for you.",
                })}
              </p>
              <div className="mt-4 space-y-2">
                <SuggestionChip
                  text={t('videoAssistant.suggestion1', {
                    defaultValue: 'Make a 2-minute video about coffee',
                  })}
                  onClick={(text) => {
                    setInputText(text);
                    inputRef.current?.focus({ preventScroll: true });
                  }}
                />
                <SuggestionChip
                  text={t('videoAssistant.suggestion2', {
                    defaultValue: 'Create a vertical video about space',
                  })}
                  onClick={(text) => {
                    setInputText(text);
                    inputRef.current?.focus({ preventScroll: true });
                  }}
                />
                <SuggestionChip
                  text={t('videoAssistant.suggestion3', {
                    defaultValue: '3-minute educational video about AI',
                  })}
                  onClick={(text) => {
                    setInputText(text);
                    inputRef.current?.focus({ preventScroll: true });
                  }}
                />
                <SuggestionChip
                  text={t('videoAssistant.suggestionOther', {
                    defaultValue: 'Other, type here...',
                  })}
                  onClick={() => {
                    inputRef.current?.focus({ preventScroll: true });
                  }}
                  isOther
                />
              </div>
            </div>
          )}

          {/* Message list */}
          {messages.map((msg, idx) => (
            <MessageBubble
              key={msg.id}
              message={msg}
              t={t}
              showCursor={isStreaming && idx === messages.length - 1 && msg.role === 'assistant'}
            />
          ))}

          {/* Loading indicator */}
          {isLoading && (
            <div className="flex items-start gap-2">
              <div className="bg-secondary-800 rounded-lg px-3 py-2">
                <div className="flex gap-1">
                  <span
                    className="w-2 h-2 bg-primary-400 rounded-full animate-bounce"
                    style={{ animationDelay: '0ms' }}
                  />
                  <span
                    className="w-2 h-2 bg-primary-400 rounded-full animate-bounce"
                    style={{ animationDelay: '150ms' }}
                  />
                  <span
                    className="w-2 h-2 bg-primary-400 rounded-full animate-bounce"
                    style={{ animationDelay: '300ms' }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-900/20 border border-red-500/30 rounded-lg px-3 py-2 text-sm text-red-300">
              {error}
            </div>
          )}
        </div>

        {/* Input area */}
        <div className="px-4 py-3 border-t border-secondary-700 flex-shrink-0">
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('videoAssistant.placeholder', {
                defaultValue: 'Describe your video...',
              })}
              disabled={isLoading}
              className="flex-1 bg-secondary-800 border border-secondary-600 rounded-lg px-3 py-2 text-sm text-white placeholder-secondary-500 resize-none focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 min-h-[40px] max-h-[120px]"
              rows={1}
            />
            <button
              onClick={handleSend}
              disabled={isLoading || !inputText.trim()}
              className="flex-shrink-0 min-h-[40px] min-w-[40px] flex items-center justify-center rounded-lg bg-primary-600 hover:bg-primary-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title={t('videoAssistant.send', { defaultValue: 'Send' })}
            >
              <Send className="h-4 w-4 text-white" />
            </button>
          </div>
        </div>
      </div>

      <LoginModal isOpen={isLoginModalOpen} onClose={closeLoginModal} message={loginMessage} />
    </>
  );
});

/** Individual message bubble */
function MessageBubble({
  message,
  t,
  showCursor,
}: {
  message: ChatMessage;
  t: (key: string, opts?: Record<string, string>) => string;
  showCursor?: boolean;
}) {
  const isUser = message.role === 'user';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
          isUser ? 'bg-primary-600 text-white' : 'bg-secondary-800 text-secondary-200'
        }`}
      >
        <p className="whitespace-pre-wrap break-words">
          {message.content}
          {showCursor && <span className="animate-pulse ml-0.5">|</span>}
        </p>

        {/* Config applied indicator */}
        {message.parsedConfig && (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-green-400 border-t border-white/10 pt-1.5">
            <CheckCircle className="h-3.5 w-3.5" />
            <span>
              {t('videoAssistant.configApplied', {
                defaultValue: 'Settings applied to the form',
              })}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/** Suggestion chip shown in the welcome state */
function SuggestionChip({
  text,
  onClick,
  isOther,
}: {
  text: string;
  onClick: (text: string) => void;
  isOther?: boolean;
}) {
  return (
    <button
      onClick={() => onClick(text)}
      className={`block w-full text-left text-xs px-3 py-2 rounded-lg transition-colors border ${
        isOther
          ? 'bg-secondary-800/50 hover:bg-secondary-700 text-secondary-400 hover:text-white border-dashed border-secondary-600'
          : 'bg-secondary-800 hover:bg-secondary-700 text-secondary-300 hover:text-white border-secondary-700'
      }`}
    >
      {text}
    </button>
  );
}
